import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { groupInto, moveInto } from '../db/sections';
import type { BoardEntry, Container } from '../db/board';

/**
 * The two things a drag can do besides rearrange: put something inside
 * something else, or carry it out of here entirely.
 */
export function useBoardMoves({
  container,
  byId,
  pathOf,
  onArrive,
}: {
  container: Container;
  byId: Map<string, BoardEntry>;
  /** The address of a container, for the spring's navigation. */
  pathOf: (sectionId: string | undefined) => string;
  /** Called once the spring has landed, so the heading can say so. */
  onArrive: () => void;
}) {
  /**
   * Dropped on a sibling: onto a section it joins that section, onto anything
   * else the two become a new one.
   */
  const group = useCallback(
    async (draggedId: string, targetId: string) => {
      const dragged = byId.get(draggedId);
      const target = byId.get(targetId);
      if (dragged === undefined || target === undefined) return;

      if (target.kind === 'section') await moveInto(draggedId, dragged.kind, target.id);
      else {
        await groupInto(
          container,
          { id: draggedId, kind: dragged.kind },
          { id: targetId, kind: target.kind },
        );
      }
    },
    [byId, container],
  );

  const navigate = useNavigate();

  /**
   * Held over a crumb in the heading: the dragged thing goes there, and so do
   * you — still carrying it. Landing somewhere and then having to pick the
   * thing up again to place it was the part worth removing.
   *
   * The move is committed before the navigation rather than held until the
   * release, so that what you're looking at is true the whole way through.
   * The drag carries on over the new container, and the release decides where
   * in it this sits.
   */
  const spring = useCallback(
    async (draggedId: string, targetId: string) => {
      const dragged = byId.get(draggedId);
      if (dragged === undefined) return;

      // The room's own crumb means the top level, which has no section id.
      const section = targetId === container.roomId ? undefined : targetId;
      await moveInto(draggedId, dragged.kind, section);

      onArrive();
      void navigate(pathOf(section));
    },
    [byId, container.roomId, navigate, pathOf, onArrive],
  );

  return { group, spring };
}
